import { Link } from "@inertiajs/react"
import { type LucideIcon, Menu } from "lucide-react"
import type { CSSProperties } from "react"

import { cn } from "@/lib/utils"

export interface MobileTabItem {
  id: string
  title: string
  href: string
  icon: LucideIcon
  active: boolean
  badge?: number
}

export function normalizePath(url: string) {
  const path = url.split(/[?#]/, 1)[0] || "/"
  return path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path
}

export function isPrefixPath(path: string, href: string) {
  return path === href || path.startsWith(`${href}/`)
}

export function MobileTabBar({
  tabs,
  menuOpen,
  onMenuClick,
  onNavigate,
  label = "App",
}: {
  tabs: readonly MobileTabItem[]
  menuOpen: boolean
  onMenuClick: () => void
  onNavigate?: () => void
  label?: string
}) {
  const tabCount = tabs.length + 1
  const activeIndex = menuOpen
    ? tabs.length
    : tabs.findIndex((tab) => tab.active)

  return (
    <nav
      data-pwa-tab-bar
      data-compact={tabCount > 4 || undefined}
      className="pwa-tab-bar md:hidden"
      aria-label={label}
      style={{ "--pwa-tab-count": tabCount } as CSSProperties}
    >
      <div className="pwa-tab-bar__dock">
        <div className="pwa-tab-bar__glass">
          <div className="pwa-tab-bar__sheen" aria-hidden />
          <div className="pwa-tab-bar__items">
            {activeIndex >= 0 && (
              <div
                className="pwa-tab-bar__lens"
                aria-hidden
                style={{
                  transform: `translateX(${activeIndex * 100}%)`,
                }}
              />
            )}
            {tabs.map((tab) => {
              const Icon = tab.icon

              return (
                <Link
                  key={tab.id}
                  href={tab.href}
                  prefetch
                  aria-label={tab.title}
                  aria-current={tab.active ? "page" : undefined}
                  className={cn("pwa-tab-bar__item", tab.active && "is-active")}
                  onClick={onNavigate}
                >
                  <span className="pwa-tab-bar__icon-wrap">
                    <Icon
                      className="pwa-tab-bar__icon"
                      strokeWidth={tab.active ? 2.15 : 1.7}
                      fill={tab.active ? "currentColor" : "none"}
                      fillOpacity={tab.active ? 0.22 : 0}
                    />
                    {(tab.badge ?? 0) > 0 && (
                      <span className="pwa-tab-bar__badge" aria-hidden>
                        {tab.badge && tab.badge > 99 ? "99+" : tab.badge}
                      </span>
                    )}
                  </span>
                  <span className="pwa-tab-bar__label">{tab.title}</span>
                </Link>
              )
            })}
            <button
              type="button"
              className={cn("pwa-tab-bar__item", menuOpen && "is-active")}
              aria-label="Menu"
              aria-expanded={menuOpen}
              aria-controls="mobile-sidebar"
              onClick={onMenuClick}
            >
              <span className="pwa-tab-bar__icon-wrap">
                <Menu
                  className="pwa-tab-bar__icon"
                  strokeWidth={menuOpen ? 2.15 : 1.7}
                />
              </span>
              <span className="pwa-tab-bar__label">Menu</span>
            </button>
          </div>
        </div>
      </div>
    </nav>
  )
}
